import React, { useState, useCallback } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Paper,
  Stack,
  Divider,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { Download, Visibility } from "@mui/icons-material";
import { PDFDownloadLink } from "@react-pdf/renderer";
import InvoiceDocument from "../../InvoiceDocument";
import LineItemsTable from "./LineItemsTable";
import ProformaPreviewModal from "./ProformaPreviewModal";

const ProformaSection = ({
  bookingDetails,
  hotelProfile,
  colors,
  invoiceData,
  lineItems,
  grandTotal,
  isEditing,
  onHeaderChange,
  onLineItemChange,
  onAddItem,
  onRemoveItem,
  onToggleEditing,
  gstEnabled,
}) => {
  const [openPreview, setOpenPreview] = useState(false);

  const openPreviewModal = useCallback(() => setOpenPreview(true), []);
  const closePreviewModal = useCallback(() => setOpenPreview(false), []);

  return (
    <>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight="bold">
          Proforma Invoice
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={isEditing}
                onChange={onToggleEditing}
                color="secondary"
              />
            }
            label={<Typography variant="body2">Enable Editing</Typography>}
          />
          <Button
            variant="outlined"
            color="info"
            startIcon={<Visibility />}
            onClick={openPreviewModal}
          >
            Preview
          </Button>
          <PDFDownloadLink
            document={
              <InvoiceDocument
                invoiceData={invoiceData}
                items={lineItems}
                bookingDetails={bookingDetails}
                hotelProfile={hotelProfile}
              />
            }
            fileName={`PI_${bookingDetails?.booking_id}.pdf`}
            style={{ textDecoration: "none" }}
          >
            {({ loading }) => (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<Download />}
                disabled={loading}
              >
                {loading ? "Generating..." : "Download PDF"}
              </Button>
            )}
          </PDFDownloadLink>
        </Stack>
      </Stack>
      <Divider sx={{ mb: 3 }} />

      {/* Content */}
      <Grid container spacing={3}>
        {/* LEFT: Header Info */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, backgroundColor: colors.primary[500], height: "100%" }}>
            <Typography variant="h6" mb={2} color={colors.greenAccent[500]}>
              Invoice Details
            </Typography>
            <Stack spacing={2} mb={3}>
              <TextField
                label="Invoice Number"
                name="invoiceNumber"
                value={invoiceData.invoiceNumber}
                onChange={onHeaderChange}
                disabled={!isEditing}
                size="small"
                fullWidth
                helperText={isEditing ? "You can manually override the Invoice ID" : ""}
              />
               <TextField
                label="Guest Name"
                name="guestName"
                value={bookingDetails.name}
                // onChange={onHeaderChange}
                disabled
                size="small"
                fullWidth
                // helperText={isEditing ? "You can manually override the Guest Name" : ""}
              />
            </Stack>
            <Divider sx={{ mb: 2 }} />

            <Typography variant="h6" mb={2} color={colors.greenAccent[500]}>
              Bill To Details
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Name"
                name="guestName"
                value={invoiceData.guestName}
                onChange={onHeaderChange}
                disabled={!isEditing}
                size="small"
                fullWidth
              />
              <TextField
                label="Address"
                name="guestAddress"
                value={invoiceData.guestAddress}
                onChange={onHeaderChange}
                disabled={!isEditing}
                multiline
                rows={3}
                size="small"
                fullWidth
              />
              <TextField
                label="Mobile"
                name="guestMobile"
                value={invoiceData.guestMobile}
                onChange={onHeaderChange}
                disabled={!isEditing}
                size="small"
                fullWidth
              />
              <TextField
                label="GSTIN"
                name="guestGstin"
                value={invoiceData.guestGstin}
                onChange={onHeaderChange}
                disabled={!isEditing}
                size="small"
                fullWidth
                placeholder="Optional"
              />
            </Stack>
          </Paper>
        </Grid>

        {/* RIGHT: Line Items Table */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 0, backgroundColor: colors.primary[500], overflow: "hidden" }}>
            <LineItemsTable
              lineItems={lineItems}
              isEditing={isEditing}
              grandTotal={grandTotal}
              colors={colors}
              onLineItemChange={onLineItemChange}
              onAddItem={onAddItem}
              onRemoveItem={onRemoveItem}
              gstEnabled={gstEnabled}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Preview Modal */}
      <ProformaPreviewModal
        open={openPreview}
        onClose={closePreviewModal}
        colors={colors}
        invoiceData={invoiceData}
        lineItems={lineItems}
        bookingDetails={bookingDetails}
        hotelProfile={hotelProfile}
        gstEnabled={gstEnabled}
      />
    </>
  );
};

export default ProformaSection;